import {Article, BIG_ONE, bigToString, ProductPricing, toBig} from 'quote-domain-core';
import { Big } from 'big.js';
import {PricingItemCorridorContext} from "./pricing-item-corridor-context";
import {singleton} from "tsyringe";

@singleton()
export class VtArticleCorridorCalculator {
    public calculate(article: Article, context: PricingItemCorridorContext, userInputPricingItem: any): void {
        const referentialService = context.referentialService;
        const region = context.region;
        const contractInfoInputs = context.contractInfoInputs;
        const contractDuration = contractInfoInputs?.contractDuration;
        const numberOfChange = userInputPricingItem?.numberOfChange?.value;

        if (context.totalStockForAllArticles.eq(0)) {
            return;
        }

        if (!article.productPricings?.some(pp => !!pp)) {
            article.productPricings = this.reconstituteProductPricings(article);
        }

        try {
            this.validateParams(article, region, contractDuration);
            this.calculateLogisticPrice(article, context.logisticPricePerService, context.totalStockForAllArticles);
            this.calculateGreenPrice(article, referentialService, region, contractInfoInputs, numberOfChange, context.totalStockForAllArticles);
            this.calculateBlackPrice(article, referentialService, contractInfoInputs, userInputPricingItem, context.totalStockForAllArticles);
        } catch (error) {
            console.error(`Erreur lors du calcul de prix vert ou le prix noir pour l'article ${article.codeElis}`, error, {
                family: article.articleDetails?.['family'],
                ppe: article.ppe,
                subFamily: article.articleDetails?.['subFamily'],
                contractDuration,
                region,
                numberOfChange,
                stockVT: userInputPricingItem?.stockVT
            });
        }

        if (!article.productPricings?.length) {
            throw new Error('Product pricing is empty');
        }

        article.productPricings = article.productPricings.map(pp => pp ? pp.recalculateCorridors(article.greenPrice, article.blackPrice) : pp);
    }

    private reconstituteProductPricings(article: Article): ProductPricing[] {
        const info = article.initialArticleInfo;
        if (!info) {
            return article.productPricings;
        }
        return [Object.assign(new ProductPricing(), {
            productPricingDetails: {
                frequency: info.frequencies,
                billingMode: info.invoicingModes,
                numberOfChange: info.numberOfChanges,
                contractDuration: info.contractDuration,
                quantityMin: info.quantityMin,
                quantityMax: info.quantityMax
            }
        })];
    }

    public applyPccPricing(article: Article): void {
        const coeff: Big = new Big('1');
        if (!article.pcc || !article.productPricings?.length) return;
        const pcc = new Big(article.pcc);
        const catalogPrice = new Big(article.catalogPrice);
        article.productPricings = article.productPricings.map(pp => pp.recalculateFromPcc(pcc, catalogPrice, coeff));
    }


    private calculateLogisticPrice(article: Article, logisticPricePerService: Big, totalStockForAllArticles: Big): void {
        const logisticPricePerArticle = logisticPricePerService.div(totalStockForAllArticles);
        Object.assign(article, { logisticPrice: bigToString(logisticPricePerArticle) });
    }

    private calculateGreenPrice(article: Article, referentialService: any, region: string, contractInfoInputs: any, nbChanges: any, totalStockForAllArticles: Big): void {
        Object.assign(article, { greenPrice: undefined });

        const contractDuration = contractInfoInputs?.contractDuration;
        const logisticCost = toBig(article.logisticPrice);
        const coefficientPccType = this.calculateCoefficientPccType(article);
        const coefficientContractDuration = this.calculateCoefficientContractDuration(referentialService, coefficientPccType, contractDuration);
        const coefficientBuyBack = this.calculateCoefficientBuyBack(article, referentialService, coefficientPccType, contractDuration);
        const coefficientNbChanges = this.calculateCoefficientNbChanges(referentialService, coefficientPccType, nbChanges);
        const coefficientWashRate = this.calculateCoefficientWashRate(article, referentialService, coefficientPccType);
        const coefficientRegion = this.calculateCoefficientRegion(referentialService, coefficientPccType, region);
        const coefficientFinContractVR = this.calculateFinContractVR(article, contractInfoInputs?.finContratVR, referentialService?.coefficientVR, contractDuration);
        const coefficientRachatVR = this.calculateCoefficientRachatVR(contractInfoInputs?.rachatVR, totalStockForAllArticles);
        const greenPrice = new Big(article.catalogPrice)
            .times(coefficientContractDuration)
            .times(coefficientBuyBack)
            .times(coefficientNbChanges)
            .times(coefficientWashRate)
            .times(coefficientRegion)
            .plus(logisticCost)
            .plus(coefficientFinContractVR)
            .plus(coefficientRachatVR);

        Object.assign(article, { greenPrice: bigToString(greenPrice) });
    }

    private calculateCoefficientPccType(article: Article): string {
        const pccTypeToCoeffKey: Record<string, string> = {
            LOW: 'coeffLowPcc',
            MEDIUM: 'coeffMediumPcc',
            HIGH: 'coeffHighPcc'
        };
        const coeffKey = pccTypeToCoeffKey[article.pccType];

        if (!coeffKey) {
            throw new Error(`Impossible de calculer le PccType : valeur invalide "${article.pccType}". Valeurs attendues : LOW, MEDIUM ou HIGH.`);
        }
        return coeffKey;
    }

    private calculateCoefficientContractDuration(referentialService: any, pccType: string, contractDuration: string): Big {
        if (!pccType || !referentialService?.contract?.length || contractDuration == null) {
            throw new Error('Impossible de calculer le CoefficientContractDuration: pccType, contract ou contractDuration manquant');
        }

        const contractDurationInYears = new Big(contractDuration).div(12);
        const matchedContract = referentialService.contract.find((contract: any) => {
            if (contract?.contractDuration == null) {
                return false;
            }
            return contractDurationInYears.eq(new Big(contract.contractDuration));
        });

        if (matchedContract?.[pccType] == null) {
            throw new Error(`Impossible de calculer le CoefficientContractDuration: coefficient contractDuration introuvable pour contractDuration=${contractDuration}, pccType=${pccType}`);
        }

        return toBig(matchedContract[pccType]);
    }

    private calculateCoefficientBuyBack(article: Article, referentialService: any, pccType: string, contractDuration: any): Big {
        if (!pccType || !referentialService?.byBack?.length) {
            throw new Error('Impossible de calculer le calculateCoefficientBuyBack: pccType ou referentialService.byBack manquant');
        }
        const buyBackValue = this.calculateBuyBackValue(article, contractDuration);

        const matchedByBack = referentialService.byBack.find((item: any) => {
            if (item?.minByBack == null || item?.maxByBack == null) {
                return false;
            }
            const min = new Big(item.minByBack);
            const max = new Big(item.maxByBack);
            return buyBackValue.gte(min) && buyBackValue.lt(max);
        });

        if (matchedByBack?.[pccType] == null) {
            throw new Error(`Impossible de calculer le calculateCoefficientBuyBack: coefficient buyBack introuvable pour pccType=${pccType}, contractDuration=${contractDuration}`);
        }

        return toBig(matchedByBack[pccType]);
    }

    private calculateBuyBackValue(article: Article, contractDuration: any): Big {
        const contractDurationInDays = new Big(contractDuration).div(12).times(365);
        const lifetime = new Big(article.washLifetime);
        const calculatedValue = toBig(contractDurationInDays).div(lifetime);
        return calculatedValue.gt(1) ? calculatedValue : BIG_ONE;
    }

    private calculateFinContractVR(article: Article, finContractVR: any, coefficientVR: any, contractDuration: any): Big {
        if (finContractVR == 'none') {
            const replacementPrice = toBig(article.replacementPrice);
            const contractDurationInWeek = toBig(contractDuration).div(12).times(52);
            return new Big(0.35).times(replacementPrice.times(toBig(coefficientVR))).div(contractDurationInWeek);
        }
        return new Big(0);
    }

    private calculateCoefficientNbChanges(referentialService: any, pccType: string, nbChanges: any): Big {
        if (!pccType || !referentialService?.changeStockNb?.length) {
            throw new Error('Impossible de calculer le CoefficientNbChanges: pccType ou referentialService.changeStockNb manquant');
        }
        const changeStockNb = referentialService.changeStockNb;
        if (changeStockNb?.length) {
            const matchedNbChanges = changeStockNb.find((item: any) => item.changeNb == nbChanges);
            if (matchedNbChanges?.[pccType] == null) {
                throw new Error(`Impossible de calculer le CoefficientNbChanges: coefficient nbChanges introuvable pour pccType=${pccType}, nbChanges=${nbChanges}`);
            }
            return toBig(matchedNbChanges[pccType]);
        }
        throw new Error('Impossible de calculer le CoefficientNbChanges: referentialService.changeStockNb vide');
    }

    private calculateCoefficientWashRate(article: Article, referentialService: any, pccType: string): Big {
        if (!pccType || !referentialService?.wash?.length) {
            throw new Error('Impossible de calculer le CoefficientWashRate: pccType ou referentialService.wash manquant');
        }

        const washRate = toBig(article.washRate);
        const matchedByWashRate = referentialService.wash.find((item: any) => {
            if (item?.minWash == null || item?.maxWash == null) {
                return false;
            }
            const min = new Big(item.minWash);
            const max = new Big(item.maxWash);
            return washRate.gte(min) && washRate.lt(max);
        });

        if (matchedByWashRate?.[pccType] == null) {
            throw new Error(`Impossible de calculer le CoefficientWashRate: coefficient washRate introuvable pour pccType=${pccType}`);
        }
        return toBig(matchedByWashRate[pccType]);
    }

    private calculateCoefficientRegion(referentialService: any, pccType: string, region: string): Big {
        if (!pccType || !referentialService?.region?.length) {
            throw new Error('Impossible de calculer le CoefficientRegion: pccType ou referentialService.region manquant');
        }
        const regions = referentialService.region;
        if (regions?.length) {
            const matchedRegion = regions.find((item: any) => item.region == region);
            if (matchedRegion?.[pccType] == null) {
                throw new Error(`Impossible de calculer le CoefficientRegion: coefficient region introuvable pour pccType=${pccType}, region=${region}`);
            }
            return toBig(matchedRegion[pccType]);
        }
        throw new Error('Impossible de calculer le CoefficientRegion: referentialService.region vide');
    }

    private calculateBlackPrice(article: Article, referentialService: any, contractInfoInputs: any, userInputsPricingItem: any, totalStockForAllArticles: Big): void {
        Object.assign(article, { blackPrice: undefined });
        const contractDuration = contractInfoInputs?.contractDuration;
        const coefficientGrossMarginRate = this.calculateCoefficientGrossMarginRate(article);
        const coefficientWashRate = toBig(article.washRate);
        const coefficientChanges = this.calculateCoefficientChanges(userInputsPricingItem?.numberOfChange?.value, userInputsPricingItem?.stockVT);
        const coefficientUnitCosts = this.calculateCoefficientUnitCosts(referentialService?.cost);

        const productionCost = coefficientWashRate.times(coefficientChanges).times(coefficientUnitCosts);
        const contractDurationInWeek = toBig(contractDuration).div(12).times(52);
        const laundryCost = toBig(article.pcc).times(this.calculateBuyBackValue(article, contractDuration).div(contractDurationInWeek));
        const logisticCost = toBig(article.logisticPrice);
        const coefficientStructure = this.calculateCoefficientStructure(this.findCostValue(referentialService?.cost, 'otherCost'));
        const indirectCost = productionCost.plus(laundryCost).plus(logisticCost).times(coefficientStructure);
        const coefficientFinContractVR = this.calculateFinContractVR(article, contractInfoInputs?.finContratVR, referentialService?.coefficientVR, contractDuration);
        const coefficientRachatVR = this.calculateCoefficientRachatVR(contractInfoInputs?.rachatVR, totalStockForAllArticles);
        const blackPrice = coefficientGrossMarginRate.times(productionCost.plus(laundryCost).plus(indirectCost))
            .plus(logisticCost)
            .plus(coefficientFinContractVR)
            .plus(coefficientRachatVR);

        Object.assign(article, { blackPrice: bigToString(blackPrice) });
    }

    private calculateCoefficientStructure(percentageStructure: any): Big {
        const parsedPercentageStructure = toBig(percentageStructure);
        const denominator = BIG_ONE.minus(parsedPercentageStructure);
        if (denominator.lte(0)) {
            throw new Error(`Impossible de calculer le CoefficientStructure: percentageStructure invalide (${percentageStructure.toString()})`);
        }
        return parsedPercentageStructure.div(denominator);
    }

    private calculateCoefficientGrossMarginRate(article: Article): Big {
        return BIG_ONE.div(BIG_ONE.minus(toBig(article.grossMarginRate)));
    }

    private calculateCoefficientChanges(nbChanges: any, stock: any): Big {
        if (nbChanges == null || stock == null || stock == 0) {
            throw new Error('Impossible de calculer le CoefficientChanges: nbChanges ou stock manquant');
        }

        if (nbChanges === 'groupé') {
            return BIG_ONE.div(new Big(2));
        }

        if (typeof nbChanges === 'string' && nbChanges.startsWith('1')) {
            nbChanges = 1;
        }

        return toBig(nbChanges).div(toBig(stock));
    }

    private calculateCoefficientUnitCosts(cost: any): Big {
        if (!cost?.length) {
            throw new Error('Impossible de calculer le CoefficientUnitCosts: referentialService.cost manquant');
        }
        return toBig(this.findCostValue(cost, 'energyCost'))
            .plus(toBig(this.findCostValue(cost, 'factoryCost')))
            .plus(toBig(this.findCostValue(cost, 'maintenanceCost')))
            .plus(toBig(this.findCostValue(cost, 'waterCost')));
    }

    private findCostValue(cost: any, name: string): any {
        return cost?.find((item: any) => item.name === name)?.value;
    }

    private validateParams(article: Article, region: string, contractDuration: any): void {
        if (!article.grossMarginRate || !article.washRate || !article.washLifetime || !region) {
            throw new Error(`Impossible de calculer le prix vert ou le prix noir: grossMarginRate, washRate, region ou washLifetime manquant pour l'article ${article.codeElis}`);
        }
        if (!contractDuration || toBig(contractDuration).eq(0)) {
            throw new Error(`Impossible de calculer le prix vert ou le prix noir: contractDuration manquant ou égal à 0 pour l'article ${article.codeElis}`);
        }
    }

    private calculateCoefficientRachatVR(rachatVR: any, totalStockForAllArticles: Big): Big {
        return toBig(rachatVR).div(totalStockForAllArticles);
    }
}

